import React, { useState, useEffect, useMemo } from 'react';
import { LuSparkles, LuArrowRight, LuBookOpen, LuGlobe } from 'react-icons/lu';
import Icon from '@amzn/awsui-components-core/icon';
import { McpConfig, LogoConfig } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { AppLogo } from 'src/components/generic-components/AppLogo';
import InstallTerminal from './InstallTerminal';
import styles from './McpLandingPage.module.scss';

const ROTATING_PHRASES = [
  'Upgrade your workflow',
  'Query data conversationally',
  'Automate the boring stuff',
  'Ship reports in seconds',
  'Let AI do the heavy lifting',
  'No MDX required'
];

const DECISION_GUIDE = [
  { useCase: 'Try an MCP now, no setup', best: 'Quick Online (Q Browser)', href: 'https://q.us-east-1.amazonaws.com' },
  { useCase: 'Local files + Excel + Gandalf', best: 'Quick Desktop (Mac/Win)', href: 'https://w.amazon.com/bin/view/DasFinSuite/QuickDesktop' },
  { useCase: 'Full AIM ecosystem + all MCPs', best: 'Local Install (WSL/Mac)', href: 'https://docs.hub.amazon.dev/mcp' },
  { useCase: 'Scheduled exports to SharePoint', best: 'ClawSpaces', href: 'https://agentspaces.amazon.dev' },
  { useCase: '24/7 monitoring + Slack + memory', best: 'MeshClaw (Cloud Desktop)', href: 'https://w.amazon.com/bin/view/DasFinSuite/MeshClaw' }
];

const RESOURCES = [
  // TODO: Replace with real Slack channel URL when available
  { label: 'MCP Guidance', context: 'AIM CLI docs on BuilderHub', href: 'https://docs.hub.amazon.dev/mcp', icon: LuBookOpen },
  { label: 'MCP Registry', context: 'Browse all available MCPs', href: 'https://ai-registry.amazon.dev', icon: LuGlobe }
];

interface McpLandingPageProps {
  servers: McpConfig[];
  onSelectServer: (serverId: string) => void;
}

const McpLandingPage: React.FC<McpLandingPageProps> = ({ servers, onSelectServer }) => {
  const { parsedApplications } = useAppContext();
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Resolve app logos: match MCP server to its parent application by mcpPath or name
  const appLogoMap = useMemo((): Record<string, LogoConfig> => {
    const map: Record<string, LogoConfig> = {};
    const activeApps = parsedApplications.filter((a) => a.isActive && a.logoConfig);
    servers.forEach((server) => {
      // Match by mcpPath on the application (e.g. /mcp/prophecy)
      const matchByPath = activeApps.find((app) => app.mcpPath === `/mcp/${server.id}`);
      if (matchByPath?.logoConfig) {
        map[server.id] = matchByPath.logoConfig;
        return;
      }
      // Fallback: match by name keyword
      const keyword = server.name.split(' ')[0].toLowerCase();
      const matchByName = activeApps.find((app) => app.applicationName.toLowerCase().includes(keyword));
      if (matchByName?.logoConfig) {
        map[server.id] = matchByName.logoConfig;
      }
    });
    return map;
  }, [parsedApplications, servers]);

  // Build combined "Install All" commands from all servers' terminal commands
  const allInstallCommands = useMemo(() => {
    // Collect all unique commands, grouped by OS + method, joined with &&
    const byOsMethod = new Map<string, string[]>();
    servers.forEach((server) => {
      (server.terminalCommands || []).forEach((cmd) => {
        const osList = Array.isArray(cmd.os) ? cmd.os : [cmd.os];
        osList.forEach((os) => {
          const key = `${os}|||${cmd.method}`;
          if (!byOsMethod.has(key)) byOsMethod.set(key, []);
          byOsMethod.get(key)!.push(cmd.command);
        });
      });
    });

    // Build flattened commands joining all server commands per OS+method
    return Array.from(byOsMethod.entries()).map(([key, commands]) => {
      const [os, method] = key.split('|||');
      const unique = [...new Set(commands)];
      return { os: [os], method, command: unique.join(' && ') };
    });
  }, [servers]);

  useEffect(() => {
    const currentPhrase = ROTATING_PHRASES[phraseIndex];
    let timeout: ReturnType<typeof setTimeout>;

    if (!isDeleting) {
      if (displayed.length < currentPhrase.length) {
        timeout = setTimeout(() => setDisplayed(currentPhrase.slice(0, displayed.length + 1)), 40);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 2000);
      }
    } else {
      if (displayed.length > 0) {
        timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 25);
      } else {
        setIsDeleting(false);
        setPhraseIndex((prev) => (prev + 1) % ROTATING_PHRASES.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayed, isDeleting, phraseIndex]);

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.greeting}>
          <span className={styles.greetingGradient}>MCP Servers</span>
          <span className={styles.rotatingLine}>
            <span className={styles.rotatingText}>{displayed}</span>
            <span className={styles.rotatingCursor} aria-hidden="true" />
          </span>
        </div>
        <p className={styles.subtitle}>Connect AI assistants to internal data tools — query, automate, and manage through natural conversation.</p>
      </div>

      {/* Server Cards */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          Available Servers
          <span className={styles.badge}>{servers.length}</span>
        </h4>
        <div className={styles.cardsRow}>
          {servers.map((server) => (
            <button key={server.id} className={styles.serverCard} onClick={() => onSelectServer(server.id)} type="button">
              <div className={styles.cardPreview}>
                {appLogoMap[server.id] ? (
                  <span className={styles.cardPreviewLogoWrapper}>
                    <AppLogo logoConfig={appLogoMap[server.id]} />
                  </span>
                ) : server.logoConfig ? (
                  <span className={styles.cardPreviewLogoWrapper}>
                    <AppLogo logoConfig={server.logoConfig} />
                  </span>
                ) : (
                  <LuSparkles className={styles.cardPreviewIcon} aria-hidden="true" />
                )}
              </div>
              <div className={styles.cardBody}>
                <div className={styles.cardText}>
                  <span className={styles.cardName}>{server.name}</span>
                  <span className={styles.cardDescription}>{server.description || ''}</span>
                </div>
                <LuArrowRight className={styles.cardArrow} aria-hidden="true" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Install All — dynamically built from all server configs */}
      {allInstallCommands.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Install All Servers</h4>
          <p className={styles.subtitle} style={{ marginBottom: 12 }}>
            Install all available MCP servers with a single command.
          </p>
          <div className={styles.terminalWrapper}>
            <InstallTerminal commands={allInstallCommands} />
          </div>
        </div>
      )}

      {/* Two-column: Decision Guide + Resources */}
      <div className={styles.bottomGrid}>
        {/* Quick Decision Guide */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Quick Decision Guide</h4>
          <div className={styles.guideTable}>
            <div className={styles.guideHeader}>
              <span>Use Case</span>
              <span>Best Option</span>
            </div>
            {DECISION_GUIDE.map((row) => (
              <div key={row.useCase} className={styles.guideRow}>
                <span className={styles.guideUseCase}>{row.useCase}</span>
                <a href={row.href} target="_blank" rel="noopener noreferrer" className={styles.guideBest}>
                  {row.best}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Resources */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Resources</h4>
          <div className={styles.resourcesList}>
            {RESOURCES.map((resource) => (
              <a key={resource.href} href={resource.href} target="_blank" rel="noopener noreferrer" className={styles.resourceLink}>
                <span className={styles.resourceIcon}>{resource.icon && <resource.icon aria-hidden="true" />}</span>
                <div className={styles.resourceText}>
                  <span className={styles.resourceLabel}>{resource.label}</span>
                  <span className={styles.resourceContext}>{resource.context}</span>
                </div>
                <Icon name="external" size="small" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default McpLandingPage;
